import React, { useState, useEffect } from 'react';
import { Save, Settings, Activity } from 'lucide-react';
import { Button } from '../../components/common/Button';
import { categoriesApi } from '../../services/api';
// Category type import removed - not needed without parent category field

interface CategoryEditProps {
  categoryId: string;
  onNavigate: (page: string) => void;
}

export const CategoryEdit: React.FC<CategoryEditProps> = ({ categoryId, onNavigate }) => {
  const [formData, setFormData] = useState({
    name: '',
    parent_category_id: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isNetworkError, setIsNetworkError] = useState(false);
  // Categories and loading state removed - parent category field is hidden
  // const [categories, setCategories] = useState<Category[]>([]);
  // const [loadingCategories, setLoadingCategories] = useState(true);

  useEffect(() => {
    loadCategory();
    // loadCategories() call removed - parent category field is hidden
  }, [categoryId]);

  // Debug: Log when formData changes
  useEffect(() => {
    console.log('FormData changed:', formData);
  }, [formData]);

  // loadCategories function removed - parent category field is hidden
  // const loadCategories = async () => { ... };

  const loadCategory = async () => {
    setLoading(true);
    setError(null);
    setIsNetworkError(false);

    try {
      console.log('Loading category with ID:', categoryId);
      const response = await categoriesApi.getById(categoryId);

      console.log('Category API response:', response);

      if (response.success && response.data) {
        const category = response.data;
        console.log('Setting form data with category:', category);
        console.log('Category name:', category.name);
        console.log('Category parent_category_id:', category.parent_category_id);

        const newFormData = {
          name: category.name || '',
          parent_category_id: category.parent_category_id || '',
        };

        console.log('New form data being set:', newFormData);
        setFormData(newFormData);
      } else {
        console.error('Failed to load category:', response.message);
        setError(response.message || 'Failed to load category');

        // Check if this is a network/connectivity error
        if (response.message?.includes('Failed to fetch') ||
          response.message?.includes('Network error') ||
          response.message?.includes('fetch')) {
          setIsNetworkError(true);
        }
      }
    } catch (error) {
      console.error('Error loading category:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to load category. Please try again.';
      setError(errorMessage);

      // Check if this is a network/connectivity error
      if (errorMessage.includes('Failed to fetch') ||
        errorMessage.includes('Network error') ||
        errorMessage.includes('fetch')) {
        setIsNetworkError(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      alert('Please provide a category name');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      console.log('Updating category with data:', formData);
      const response = await categoriesApi.update(categoryId, {
        name: formData.name.trim(),
        parent_category_id: formData.parent_category_id || undefined,
        // Note: description is not supported by the backend API yet
      });

      if (response.success) {
        console.log('Category updated successfully');
        onNavigate('categories');
      } else {
        console.error('Failed to update category:', response.message);
        setError(response.message || 'Failed to update category');
      }
    } catch (error) {
      console.error('Error updating category:', error);
      setError('Failed to update category. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-center min-h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-burgundy-600 mx-auto mb-4"></div>
              <p className="text-navy-600">Loading category...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="text-center py-12">
            <div className="bg-red-100 p-3 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {isNetworkError ? 'API Connection Error' : 'Error Loading Category'}
            </h3>
            <p className="text-gray-600 mb-6">{error}</p>

            {isNetworkError && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-left">
                <div className="flex items-center space-x-2 mb-2">
                  <Activity className="w-4 h-4 text-blue-600" />
                  <span className="text-sm font-medium text-blue-800">Connection Issue Detected</span>
                </div>
                <p className="text-sm text-blue-700 mb-3">
                  This error typically occurs when the backend API server is not running or not accessible.
                  The System Status page provides detailed setup instructions to resolve this issue.
                </p>
                <Button
                  size="sm"
                  onClick={() => onNavigate('api-test')}
                  icon={Settings}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Go to System Status & Setup
                </Button>
              </div>
            )}

            <div className="flex items-center justify-center space-x-4">
              <Button
                variant="outline"
                onClick={() => onNavigate('categories')}
              >
                Back to Categories
              </Button>
              <Button
                onClick={loadCategory}
              >
                Try Again
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-navy-900 mb-2">Edit Category</h2>
          <p className="text-navy-600">Update category name and description</p>
          {categoryId && (
            <p className="text-sm text-navy-500 mt-1">Category ID: {categoryId}</p>
          )}
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Category Name */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-navy-700 mb-2">
              Category Name *
            </label>
            <input
              type="text"
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Clothing, Electronics, Accessories"
              className="w-full px-3 py-2 border border-navy-300 rounded-md focus:outline-none focus:ring-2 focus:ring-burgundy-500 focus:border-transparent input-field"
              required
            />
          </div>

          {/* Parent Category - HIDDEN: Temporarily disabled due to functionality issues */}
          {/* 
          <div>
            <label htmlFor="parent_category_id" className="block text-sm font-medium text-navy-700 mb-2">
              Parent Category
            </label>
            <select
              id="parent_category_id"
              value={formData.parent_category_id}
              onChange={(e) => setFormData({ ...formData, parent_category_id: e.target.value })}
              className="w-full px-3 py-2 border border-navy-300 rounded-md focus:outline-none focus:ring-2 focus:ring-burgundy-500 focus:border-transparent input-field"
              disabled={loadingCategories}
            >
              <option value="">None (Top-level category)</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-navy-500">
              Optional: Choose a parent category to make this a subcategory
            </p>
          </div>
          */}

          {/* Action Buttons */}
          <div className="flex items-center justify-end space-x-4 pt-6 border-t border-navy-200">
            <Button
              type="button"
              variant="outline"
              onClick={() => onNavigate('categories')}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={saving}
              icon={Save}
            >
              Update Category
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};